/**
 * RideCoordinatorPage · v2.7.65+E147
 * ═════════════════════════════════════════════════════════════════════
 * Admin-side wrapper of the New Trip flow with an active-driver picker.
 * Intake staff fills the form during a call, picks the target driver
 * from a live-heartbeat roster, and hits "Dispatch" — the driver's
 * Queued Rides tile lights up instantly (WS broadcast).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Loader2, MapPin, User, Save, Radio, Phone, ChevronUp, ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { fmtUsd } from "@/lib/format";
import LiveQueueColumn from "@/components/admin/LiveQueueColumn";


export default function RideCoordinatorPage() {
  const [rateTable, setRateTable] = useState(null);
  const [drivers, setDrivers]     = useState([]);
  const [loading, setLoading]     = useState(true);

  // Form state (mirrors NewTripPage). Kept intentionally lean here so the
  // coordinator screen focuses on the intake conversation.
  const [rider, setRider]     = useState("");
  const [phone, setPhone]     = useState("");
  const [pickup, setPickup]   = useState("");
  const [dropoffKey, setDropoffKey] = useState("weatherford-in-city");
  const [dropoffAddr, setDropoffAddr] = useState("");
  const [pax, setPax]         = useState(1);
  const [round, setRound]     = useState(false);
  const [notes, setNotes]     = useState("");
  const [driverId, setDriverId]   = useState("");
  const [driverName, setDriverName] = useState("");

  // v2.8+E294 · Collapse the Dispatch form to a slim bar so the Live Queue is
  // front-and-centre during busy shifts. Persisted per-browser.
  const [formCollapsed, setFormCollapsed] = useState(() => {
    try { return localStorage.getItem("rtg_rc_form_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("rtg_rc_form_collapsed", formCollapsed ? "1" : "0"); } catch { /* quota */ }
  }, [formCollapsed]);

  // v2.7.65+E150 · CCVAP voucher on the coordinator form.
  const [voucherCode,   setVoucherCode]   = useState("");
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [voucherBusy,   setVoucherBusy]   = useState(false);

  const [quote, setQuote]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [lastQueued, setLastQueued] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [rt, dv] = await Promise.all([
          adminApi.get("/pricing/new-trip/rate-table"),
          adminApi.get("/admin/queued-trips/active-drivers"),
        ]);
        setRateTable(rt.data);
        setDrivers(dv.data.drivers || []);
      } catch (e) {
        toast.error("Failed to load coordinator data");
      } finally { setLoading(false); }
    })();
  }, []);

  const runQuote = useCallback(async () => {
    if (!rateTable) return;
    try {
      const { data } = await adminApi.post("/pricing/new-trip/quote", {
        dropoff_key: dropoffKey,
        passenger_count: pax,
        round_trip: round,
      });
      setQuote(data);
    } catch (e) {
      setQuote(null);
    }
  }, [rateTable, dropoffKey, pax, round]);

  useEffect(() => {
    const t = setTimeout(runQuote, 250);
    return () => clearTimeout(t);
  }, [runQuote]);

  // v2.7.65+E150 · Real-time CCVAP verify (same debounced pattern as
  // NewTripPage, share the /vouchers/verify endpoint).
  useEffect(() => {
    if (!voucherCode.trim()) { setVoucherStatus(null); return undefined; }
    const t = setTimeout(async () => {
      setVoucherBusy(true);
      try {
        const { data } = await adminApi.post("/vouchers/verify", {
          serial_number: voucherCode.trim(),
        });
        setVoucherStatus(data);
      } catch (e) {
        setVoucherStatus({ valid: false, reason: e?.response?.data?.detail || "Verify failed" });
      } finally { setVoucherBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [voucherCode]);

  const dispatch = async () => {
    if (!rider.trim() || !pickup.trim() || !driverId) {
      toast.error("Rider name, pickup, and driver are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/queued-trips", {
        rider_name:       rider.trim(),
        rider_phone:      phone.trim(),
        pickup_address:   pickup.trim(),
        dropoff_key:      dropoffKey,
        dropoff_address:  dropoffAddr.trim() || null,
        passenger_count:  pax,
        round_trip:       round,
        notes:            notes.trim() || null,
        assigned_driver_id:   driverId,
        assigned_driver_name: driverName,
        // v2.7.65+E150 · CCVAP serial + auto discount when valid.
        voucher_code:     voucherCode.trim() || null,
        discount_kind:    (voucherCode.trim() && voucherStatus?.valid) ? "Account voucher" : null,
        discount_amount:  (voucherCode.trim() && voucherStatus?.valid) ? (voucherStatus.remaining_balance_usd || 0) : 0,
      });
      toast.success(`Dispatched to ${driverName || driverId} · ${fmtUsd(data.total || 0)}`);
      setLastQueued(data);
      // Reset call-taking fields, keep the driver + destination for the next call in a stream.
      setRider(""); setPhone(""); setPickup(""); setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    } finally { setSaving(false); }
  };

  const onlineCount = useMemo(() => drivers.filter((d) => d.online).length, [drivers]);

  if (loading) return <div className="p-8 text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/>Loading Ride Coordinator…</div>;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-6" data-testid="ride-coordinator-page">
      <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold flex items-center gap-1.5">
            <Radio className="h-3 w-3"/>Ride Coordinator
          </div>
          <h1 className="text-xl font-black text-zinc-100">Dispatch a Ride</h1>
          {!formCollapsed && <p className="text-xs text-zinc-500 mt-0.5">Take the call, pick the driver, hit Dispatch. The driver's Queued Rides tile updates instantly.</p>}
        </div>
        <Button variant="outline" size="sm" onClick={() => setFormCollapsed((v) => !v)} data-testid="rc-collapse-toggle" className="h-8 text-xs shrink-0 border-zinc-700 text-zinc-300 hover:text-zinc-100">
          {formCollapsed ? <><ChevronDown className="h-4 w-4 mr-1"/>Expand form</> : <><ChevronUp className="h-4 w-4 mr-1"/>Collapse form</>}
        </Button>
      </div>

      {formCollapsed ? (
        <Card className="p-3 flex items-center justify-between gap-2 border-amber-500/30 bg-amber-500/[0.04]" data-testid="rc-form-collapsed-bar">
          <div className="text-xs text-zinc-400 flex items-center gap-1.5 min-w-0">
            <Radio className="h-3.5 w-3.5 text-amber-400 shrink-0"/>
            <span className="truncate">Dispatch form collapsed — queue is front-and-centre.</span>
          </div>
          <Button size="sm" onClick={() => setFormCollapsed(false)} data-testid="rc-form-expand-btn" className="h-7 text-xs bg-amber-500 hover:bg-amber-400 text-black font-bold shrink-0">
            <ChevronDown className="h-4 w-4 mr-1"/>Expand
          </Button>
        </Card>
      ) : (
      <>
      <Card className="p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5">
          <User className="h-3 w-3"/>Rider
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Rider name" value={rider} onChange={(e) => setRider(e.target.value)} data-testid="rc-rider-name"/>
          <Input placeholder="Rider phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="rc-rider-phone" type="tel"/>
        </div>
      </Card>

      <Card className="p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5">
          <MapPin className="h-3 w-3"/>Trip
        </div>
        <Input placeholder="Pickup address" value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid="rc-pickup"/>
        <select
          value={dropoffKey} onChange={(e) => setDropoffKey(e.target.value)}
          className="w-full h-10 px-3 rounded-md border border-zinc-700 bg-zinc-950 text-sm text-zinc-100"
          data-testid="rc-dropoff-key"
        >
          <optgroup label="Tier 1 · In-city">
            {rateTable.presets.filter((p) => p.tier === 1).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </optgroup>
          <optgroup label="Tier 2 · Regional">
            {rateTable.presets.filter((p) => p.tier === 2).map((p) => <option key={p.key} value={p.key}>{p.label} · ${p.base_fare}</option>)}
          </optgroup>
          <optgroup label="Tier 3 · Metro">
            {rateTable.presets.filter((p) => p.tier === 3).map((p) => <option key={p.key} value={p.key}>{p.label} · ${p.base_fare}</option>)}
          </optgroup>
        </select>
        <Input placeholder="Drop-off address (optional)" value={dropoffAddr} onChange={(e) => setDropoffAddr(e.target.value)} data-testid="rc-dropoff-addr"/>
        <div className="grid grid-cols-3 gap-3 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Passengers</div>
            <Input type="number" min="1" max="15" value={pax} onChange={(e) => setPax(parseInt(e.target.value) || 1)} data-testid="rc-pax"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Round trip</div>
            <Switch checked={round} onCheckedChange={setRound} data-testid="rc-round"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Quote</div>
            <div className="h-10 flex items-center text-lg font-black text-amber-500" data-testid="rc-quote">{quote ? fmtUsd(quote.subtotal) : "—"}</div>
          </div>
        </div>
        <Input placeholder="Notes (special instructions, gate codes, luggage, etc.)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rc-notes"/>
      </Card>

      <Card className="p-4 space-y-2" data-testid="rc-voucher-card">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">CCVAP Voucher (optional)</div>
        <Input
          value={voucherCode}
          onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
          placeholder="Voucher serial (e.g. CCVAP-042)"
          data-testid="rc-voucher-code"
        />
        {voucherCode.trim() && (
          <div
            data-testid="rc-voucher-status"
            data-voucher-valid={voucherStatus?.valid ? "true" : "false"}
            className={`text-xs px-3 py-2 rounded-md border flex items-center gap-2 ${
              voucherBusy
                ? "border-zinc-700 bg-zinc-900 text-zinc-400"
                : voucherStatus?.valid
                  ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400"
                  : "border-rose-500/50 bg-rose-500/10 text-rose-400"
            }`}
          >
            {voucherBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin"/>Verifying…</>
              : voucherStatus?.valid ? (
                <span>✓ Valid · {voucherStatus.rider_first_name || "rider"} · ${(voucherStatus.remaining_balance_usd || 0).toFixed(2)} available</span>
              ) : (
                <span>✗ {voucherStatus?.reason || "Invalid or previously redeemed CCVAP voucher"}</span>
              )}
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5">
          <Radio className="h-3 w-3"/>Driver · {onlineCount} online / {drivers.length} active
        </div>
        <select
          value={driverId}
          onChange={(e) => {
            const val = e.target.value;
            setDriverId(val);
            setDriverName((drivers.find((d) => d.id === val)?.name) || "");
          }}
          className="w-full h-10 px-3 rounded-md border border-zinc-700 bg-zinc-950 text-sm text-zinc-100"
          data-testid="rc-driver-select"
        >
          <option value="">Choose a driver…</option>
          <optgroup label="🟢 Online">
            {drivers.filter((d) => d.online).map((d) => (
              <option key={d.id} value={d.id}>{d.name} · {d.phone}</option>
            ))}
          </optgroup>
          <optgroup label="⚪ Offline / not pinging">
            {drivers.filter((d) => !d.online).map((d) => (
              <option key={d.id} value={d.id}>{d.name} · {d.phone}</option>
            ))}
          </optgroup>
        </select>
      </Card>

      <Button
        onClick={dispatch}
        disabled={saving || !driverId || !rider.trim() || !pickup.trim() || (voucherCode.trim() && voucherStatus && !voucherStatus.valid)}
        className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-black text-base"
        data-testid="rc-dispatch"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Save className="h-4 w-4 mr-2"/>}
        Dispatch to {driverName || "…"} {quote ? `· ${fmtUsd(quote.subtotal)}` : ""}
      </Button>

      {lastQueued && (
        <Card className="p-3 border-emerald-500/40 text-sm text-zinc-300" data-testid="rc-last-queued">
          <div className="text-[10px] uppercase tracking-widest text-emerald-500 font-bold">Last dispatch ✓</div>
          <div><b>{lastQueued.rider_name || "Rider"}</b> · {lastQueued.dropoff_label} · {fmtUsd(lastQueued.total)}</div>
          <div className="text-[10px] text-zinc-500">To {lastQueued.assigned_driver_name || lastQueued.assigned_driver_id}</div>
        </Card>
      )}
      </>
      )}
      </div>

      {/* v2.8+E293 · Live Queue now stacks BELOW the Dispatch form as a single
          vertical column of individual ride cards (one ride = one card, full
          width, scrolls on desktop + mobile). Auto-polling + WS unchanged. */}
      <div data-testid="ride-coordinator-queue">
        <LiveQueueColumn/>
      </div>
    </div>
  );
}
